/**
 * IndustryEVMultipleComparisonChart
 *
 * Horizontal bar with median shown as its own row.
 */
import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear } from '../../financial/utils/formatters';
import YearNavigator from './YearNavigator';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries, IndustryBenchmarkRecord } from '../../../types/industryResearch';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
const MEDIAN_COLOR = '#6366f1';

interface Props {
  companies: CompanyTimeSeries[];
  medianData: IndustryBenchmarkRecord[];
  region?: RegionConfig;
}

export default function IndustryEVMultipleComparisonChart({ companies, medianData, region }: Props) {
  const years = medianData.map((d) => d.year).sort((a, b) => a - b);
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1] ?? 0);

  const medianRec = medianData.find((d) => d.year === selectedYear);
  const medianMultiple = medianRec?.ev_ebitda != null ? parseFloat(medianRec.ev_ebitda.toFixed(1)) : null;

  const barData = [
    ...companies.map((co, i) => {
      const rec = co.data.find((d) => d.year === selectedYear);
      return {
        name: co.company_name.length > 22 ? co.company_name.slice(0, 20) + '...' : co.company_name,
        fullName: co.company_name,
        value: rec?.ev_ebitda != null ? parseFloat(rec.ev_ebitda.toFixed(1)) : null,
        color: COLORS[i % COLORS.length],
        isMedian: false,
      };
    }).sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">EV/EBITDA Multiple (x)</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Valuation multiple vs median</p>
          </div>
          <YearNavigator
            years={years}
            selectedYear={selectedYear}
            onChange={setSelectedYear}
            formatYear={(y) => formatFiscalYear(y, region) ?? `FY${y}`}
          />
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 48 + 30)}>
          <BarChart data={barData} layout="vertical" margin={{ top: 4, right: 50, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}x`} domain={[0, 'auto']} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v: number, _: string, props: any) => [
                `${v?.toFixed(1)}x`,
                props.payload.isMedian ? 'Industry Median' : 'EV/EBITDA',
              ]}
              labelFormatter={(l) => barData.find(d => d.name === l)?.fullName ?? l}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={26}
              label={{ position: 'right', fontSize: 10, fill: '#6b7280', formatter: (v: number) => v != null ? `${v}x` : '' }}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
